import { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { DashboardLayout } from '../../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Plus, Copy, ExternalLink, CheckCircle2, AlertCircle, Eye, Clock, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function V2CreateInterview() {
  const { token } = useAuth();
  const [universities, setUniversities] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [createdInterview, setCreatedInterview] = useState(null);

  const [form, setForm] = useState({
    student_name: '',
    student_email: '',
    university_id: '',
    question_count_override: '',
    reading_time_override: '',
    answering_time_override: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        const [uniRes, setRes] = await Promise.all([
          axios.get(`${API_URL}/api/universities`, { headers }),
          axios.get(`${API_URL}/api/v2/settings`, { headers })
        ]);
        setUniversities(uniRes.data);
        setSettings(setRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleCreate = async () => {
    if (!form.student_name.trim()) { toast.error('Student name is required'); return; }
    if (!form.student_email.trim()) { toast.error('Student email is required'); return; }

    setCreating(true);
    try {
      const payload = {
        student_name: form.student_name.trim(),
        student_email: form.student_email.trim(),
        university_id: form.university_id || null,
        idempotency_key: `admin-create-${Date.now()}-${Math.random().toString(36).slice(2)}`
      };
      if (form.question_count_override) payload.question_count_override = parseInt(form.question_count_override);
      if (form.reading_time_override) payload.reading_time_override = parseInt(form.reading_time_override);
      if (form.answering_time_override) payload.answering_time_override = parseInt(form.answering_time_override);

      const res = await axios.post(`${API_URL}/api/v2/interviews/create`, payload, { headers });
      setCreatedInterview(res.data);
      setResultOpen(true);
      toast.success('Test interview created!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create interview');
    } finally {
      setCreating(false);
    }
  };

  const interviewLink = createdInterview ? `${window.location.origin}/interview/v2/${createdInterview.interview_token}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(interviewLink);
    toast.success('Link copied to clipboard');
  };

  if (loading) return <DashboardLayout role="super_admin"><div className="p-8 text-center text-muted-foreground">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6 max-w-3xl" data-testid="v2-create-interview">
        <div>
          <h1 className="text-2xl font-bold">Create V2 Test Interview</h1>
          <p className="text-sm text-muted-foreground mt-1">Generate a secure interview link using the V2 engine. Test interviews do not consume packages.</p>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Student / Test Participant</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Full Name *</label>
                <Input value={form.student_name} onChange={e => setForm({...form, student_name: e.target.value})} placeholder="John Smith" data-testid="v2-create-name" />
              </div>
              <div>
                <label className="text-sm font-medium">Email *</label>
                <Input type="email" value={form.student_email} onChange={e => setForm({...form, student_email: e.target.value})} placeholder="john@example.com" data-testid="v2-create-email" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">University (optional)</label>
              <select className="w-full mt-1 p-2 border rounded-md bg-background text-sm" value={form.university_id} onChange={e => setForm({...form, university_id: e.target.value})}>
                <option value="">No specific university</option>
                {universities.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Configuration Overrides (optional)</CardTitle></CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-4">Leave blank to use V2 default settings ({settings?.exact_question_count || 15} questions, {settings?.reading_time_per_question || 60}s read, {settings?.answering_time_per_question || 90}s answer)</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Question Count</label>
                <Input type="number" min={1} max={50} value={form.question_count_override} onChange={e => setForm({...form, question_count_override: e.target.value})} placeholder={`${settings?.exact_question_count || 15}`} />
              </div>
              <div>
                <label className="text-sm font-medium">Reading Time (sec)</label>
                <Input type="number" min={10} max={300} value={form.reading_time_override} onChange={e => setForm({...form, reading_time_override: e.target.value})} placeholder={`${settings?.reading_time_per_question || 60}`} />
              </div>
              <div>
                <label className="text-sm font-medium">Answer Time (sec)</label>
                <Input type="number" min={10} max={600} value={form.answering_time_override} onChange={e => setForm({...form, answering_time_override: e.target.value})} placeholder={`${settings?.answering_time_per_question || 90}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleCreate} disabled={creating} size="lg" data-testid="v2-create-btn">
            <Plus className="w-4 h-4 mr-2" /> {creating ? 'Creating...' : 'Create Test Interview'}
          </Button>
        </div>

        {/* Result Dialog */}
        <Dialog open={resultOpen} onOpenChange={setResultOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-green-500" /> Interview Created</DialogTitle></DialogHeader>
            {createdInterview && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Interview ID</span><p className="font-mono text-xs mt-0.5">{createdInterview.interview_number}</p></div>
                  <div><span className="text-muted-foreground">Questions</span><p className="font-semibold mt-0.5">{createdInterview.planned_question_count}</p></div>
                  <div><span className="text-muted-foreground">Engine</span><Badge className="mt-0.5">V2</Badge></div>
                  <div><span className="text-muted-foreground">Status</span><Badge variant="outline" className="mt-0.5">Created</Badge></div>
                </div>

                <div>
                  <label className="text-sm font-medium">Secure Interview Link</label>
                  <div className="flex gap-2 mt-1">
                    <Input readOnly value={interviewLink} className="font-mono text-xs" data-testid="v2-interview-link" />
                    <Button variant="outline" size="sm" onClick={copyLink}><Copy className="w-4 h-4" /></Button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => window.open(interviewLink, '_blank')} data-testid="v2-open-interview">
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Open Interview
                  </Button>
                  <Button variant="outline" onClick={() => { setResultOpen(false); setForm({student_name:'',student_email:'',university_id:'',question_count_override:'',reading_time_override:'',answering_time_override:''}); }}>
                    Create Another
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
